import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStores } from '@/hooks/use-stores';
import { useDeviceDrawerActions, URL_PARAM_DEVICE_ID, URL_PARAM_MINIMIZED } from './useDeviceDrawerActions';

export const useDeviceIdFromUrl = (isMapInitialized: boolean, devicesCount: number) => {
  const [searchParams] = useSearchParams();
  const { mapBuilderStore } = useStores();
  const { openDeviceDrawer, closeDeviceDrawer, setMinimized } = useDeviceDrawerActions();

  const previousDeviceIdFromUrlRef = useRef<string | null>(null);
  const previousDeviceIdInStoreRef = useRef<string | undefined>(undefined);
  const previousMinimizedFromUrlRef = useRef<boolean | null>(null);
  const previousMinimizedInStoreRef = useRef<boolean>(false);
  const hasProcessedInitialUrlRef = useRef(false);
  const isSyncingFromUrlRef = useRef(false);

  const deviceIdFromUrl = searchParams.get(URL_PARAM_DEVICE_ID);
  const minimizedFromUrl = searchParams.get(URL_PARAM_MINIMIZED) === 'true';
  const currentDeviceIdInStore = mapBuilderStore.selectedDevice?.deviceId;
  const currentMinimizedInStore = mapBuilderStore.isDetailsModalMinimized;

  useEffect(() => {
    if (!isMapInitialized || devicesCount === 0) {
      return;
    }

    const urlDeviceIdChanged = previousDeviceIdFromUrlRef.current !== deviceIdFromUrl;
    const urlMinimizedChanged = previousMinimizedFromUrlRef.current !== minimizedFromUrl;
    previousDeviceIdFromUrlRef.current = deviceIdFromUrl;
    previousMinimizedFromUrlRef.current = minimizedFromUrl;

    if (urlDeviceIdChanged || urlMinimizedChanged) {
      isSyncingFromUrlRef.current = true;
    }

    if (deviceIdFromUrl && currentDeviceIdInStore !== deviceIdFromUrl) {
      mapBuilderStore.selectDeviceById(deviceIdFromUrl);
      if (minimizedFromUrl) {
        mapBuilderStore.setIsDetailsModalMinimized(true);
      }
      hasProcessedInitialUrlRef.current = true;
    } else if (!deviceIdFromUrl && currentDeviceIdInStore) {
      if (currentMinimizedInStore) {
        mapBuilderStore.closeMinimizedModal();
      } else {
        mapBuilderStore.closeMaximizedModal();
      }
      hasProcessedInitialUrlRef.current = true;
    } else if (!deviceIdFromUrl) {
      hasProcessedInitialUrlRef.current = true;
    }

    if (deviceIdFromUrl && currentDeviceIdInStore === deviceIdFromUrl) {
      if (minimizedFromUrl !== currentMinimizedInStore) {
        mapBuilderStore.setIsDetailsModalMinimized(minimizedFromUrl);
      }
    } else if (!deviceIdFromUrl && currentMinimizedInStore) {
      mapBuilderStore.closeMinimizedModal();
    }

    if (urlDeviceIdChanged || urlMinimizedChanged) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isSyncingFromUrlRef.current = false;
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceIdFromUrl, minimizedFromUrl, isMapInitialized, devicesCount]);

  useEffect(() => {
    if (!isMapInitialized || !hasProcessedInitialUrlRef.current) {
      return;
    }

    const storeDeviceIdChanged = previousDeviceIdInStoreRef.current !== currentDeviceIdInStore;
    const storeMinimizedChanged = previousMinimizedInStoreRef.current !== currentMinimizedInStore;
    previousDeviceIdInStoreRef.current = currentDeviceIdInStore;
    previousMinimizedInStoreRef.current = currentMinimizedInStore;

    if (isSyncingFromUrlRef.current && currentDeviceIdInStore === deviceIdFromUrl) {
      if (storeMinimizedChanged && currentDeviceIdInStore) {
        setMinimized(currentMinimizedInStore);
      }
      return;
    }

    if (storeDeviceIdChanged && currentDeviceIdInStore && currentDeviceIdInStore !== deviceIdFromUrl) {
      isSyncingFromUrlRef.current = false;
      openDeviceDrawer(currentDeviceIdInStore, currentMinimizedInStore);
    } else if (storeDeviceIdChanged && !currentDeviceIdInStore && deviceIdFromUrl) {
      if (!isSyncingFromUrlRef.current) {
        closeDeviceDrawer();
      }
    } else if (storeMinimizedChanged && currentDeviceIdInStore && !isSyncingFromUrlRef.current) {
      setMinimized(currentMinimizedInStore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDeviceIdInStore, currentMinimizedInStore, isMapInitialized, openDeviceDrawer, deviceIdFromUrl]);
};
